/**
 * Utility functions for comparing versions and highlighting differences
 */

import { createErrorLogger, ErrorType } from './errorHandler';

const logger = createErrorLogger('VersionCompare');

/**
 * Smart ID management for tracking items across versions
 * This system assigns incremental IDs and tracks them intelligently
 */

/**
 * Generate a unique ID for a new item
 * @param {Array} existingItems - Array of existing items with IDs
 * @returns {number} Next available ID
 */
const generateNextId = (existingItems) => {
  if (!existingItems || existingItems.length === 0) return 1;

  const existingIds = existingItems
    .map(item => item.id)
    .filter(id => (id !== null && id !== undefined) && !isNaN(id))
    .map(id => parseInt(id));

  return existingIds.length > 0 ? Math.max(...existingIds) + 1 : 1;
};

/**
 * Ensure all items have IDs, assigning new ones where needed
 * @param {Array} items - Array of items
 * @param {Array} previousItems - Previous version items for ID reference
 * @param {string} type - Data type ('cv', 'job', 'company')
 * @returns {Array} Items with IDs assigned
 */
const ensureItemIds = (items, previousItems = [], type) => {
  if (!items || !Array.isArray(items)) return [];

  const typeConfig = DATA_TYPES[type];
  if (!typeConfig) return items;

  const itemIdentifier = typeConfig.itemIdentifier;

  // Create a map of previous items by their content for ID lookup
  const previousItemMap = new Map();
  if (previousItems && Array.isArray(previousItems)) {
    previousItems.forEach(item => {
      if (item.id !== null && item.id !== undefined) {
        let key;
        if (type === 'cv') {
          key = item.competency || item.name || '';
        } else {
          key = item[itemIdentifier] || '';
        }
        previousItemMap.set(key, item.id);
      }
    });
  }

  // Get the next available ID
  let nextId = generateNextId([...items, ...previousItems]);

  // Assign IDs to items
  return items.map(item => {
    if (item.id !== null && item.id !== undefined) {
      // Item already has an ID
      return item;
    }

    // Check if we can find a matching previous item to preserve ID
    let key;
    if (type === 'cv') {
      key = item.competency || item.name || '';
    } else {
      key = item[itemIdentifier] || '';
    }

    const existingId = previousItemMap.get(key);
    if (existingId !== null && existingId !== undefined) {
      // Found matching previous item, use its ID
      return { ...item, id: existingId };
    }

    // Assign new ID
    const newItem = { ...item, id: nextId };
    nextId++;
    return newItem;
  });
};

/**
 * Data type configuration for different data types in the application
 * This defines the key properties and field mappings for each data type
 */
const DATA_TYPES = {
  cv: {
    itemsKey: 'competencies',
    itemIdentifier: 'competency',
    idField: 'id',
    fieldsToCompare: ['proficiency', 'category', 'evidence'],
    requiredFields: ['id', 'competency', 'category', 'proficiency', 'evidence']
  },
  job: {
    itemsKey: 'skills',
    itemIdentifier: 'name',
    idField: 'id',
    fieldsToCompare: ['importance', 'category', 'context'],
    requiredFields: ['id', 'name', 'category', 'importance', 'context']
  },
  company: {
    itemsKey: 'sections',
    itemIdentifier: 'name',
    idField: 'id',
    fieldsToCompare: ['content'],
    requiredFields: ['id', 'name', 'content']
  }
};

/**
 * Compare two arrays of items to find added, removed, and modified items using smart ID tracking
 * @param {Array} rawCurrentItems - Current version items (may not have IDs)
 * @param {Array} rawPreviousItems - Previous version items (may not have IDs)
 * @param {String} type - Type of data ('cv', 'job', or 'company')
 * @returns {Object} Object containing added, removed, and modified items
 */
export const compareVersionItems = (rawCurrentItems, rawPreviousItems, type) => {
  if (!rawCurrentItems || !rawPreviousItems) {
    logger.info('[compareVersionItems]', 'Missing data arrays', {
      hasCurrentItems: !!rawCurrentItems,
      hasPreviousItems: !!rawPreviousItems,
      currentLength: rawCurrentItems?.length || 0,
      previousLength: rawPreviousItems?.length || 0
    });
    return { added: [], removed: [], modified: [] };
  }

  // Validate the data type
  if (!DATA_TYPES[type]) {
    // For backward compatibility, check if type is 'competencies' or 'skills'
    if (type === 'competencies') {
      type = 'cv';
    } else if (type === 'skills') {
      type = 'job';
    } else if (type === 'sections') {
      type = 'company';
    } else {
      logger.error('compareVersionItems', `Invalid data type: ${type}. Using default type 'cv'.`, { type }, ErrorType.VALIDATION);
      type = 'cv';
    }
  }

  // Get the configuration for this data type
  const typeConfig = DATA_TYPES[type];

  // Ensure both arrays have proper IDs
  const previousItems = ensureItemIds(rawPreviousItems, [], type);
  const currentItems = ensureItemIds(rawCurrentItems, previousItems, type);

  logger.info('[compareVersionItems]', 'Starting ID-based comparison', {
    type,
    currentItemsLength: currentItems.length,
    previousItemsLength: previousItems.length,
    currentSample: currentItems.slice(0, 2).map(item => ({
      id: item.id,
      [typeConfig.itemIdentifier]: item[typeConfig.itemIdentifier]
    })),
    previousSample: previousItems.slice(0, 2).map(item => ({
      id: item.id,
      [typeConfig.itemIdentifier]: item[typeConfig.itemIdentifier]
    }))
  });

  // Create maps for faster lookup using IDs
  const currentMap = new Map();
  const previousMap = new Map();

  // Populate maps using IDs as keys
  currentItems.forEach(item => {
    if (item.id !== null && item.id !== undefined) {
      currentMap.set(item.id, item);
    }
  });

  previousItems.forEach(item => {
    if (item.id !== null && item.id !== undefined) {
      previousMap.set(item.id, item);
    }
  });

  // Find added, removed, and modified items
  const added = [];
  const modified = [];

  // Find added and modified items by comparing IDs
  currentItems.forEach(item => {
    if (item.id === null || item.id === undefined) {
      // Item without ID is considered new
      added.push({
        item,
        status: 'added'
      });
      return;
    }

    if (!previousMap.has(item.id)) {
      // Item with new ID is added
      added.push({
        item,
        status: 'added'
      });
    } else {
      // Item exists in both versions, check if modified
      const previousItem = previousMap.get(item.id);

      // Get the fields to compare from the type configuration (including the identifier)
      const fieldsToCompare = [typeConfig.itemIdentifier, ...typeConfig.fieldsToCompare];

      let isModified = false;
      const changes = {};

      fieldsToCompare.forEach(field => {
        // Skip comparison for certain fields
        if (['status', 'item', 'previousItem', 'changes', 'id'].includes(field)) {
          return;
        }

        // Handle undefined or null values
        const prevValue = previousItem[field] || '';
        const currValue = item[field] || '';

        if (prevValue !== currValue) {
          isModified = true;
          changes[field] = {
            from: prevValue || 'Not specified',
            to: currValue || 'Not specified'
          };
        }
      });

      if (isModified) {
        modified.push({
          item,
          previousItem,
          changes,
          status: 'modified'
        });
      }
    }
  });

  // Find removed items by checking which previous IDs are not in current
  const removed = [];
  previousItems.forEach(item => {
    if ((item.id !== null && item.id !== undefined) && !currentMap.has(item.id)) {
      removed.push({
        item,
        status: 'removed'
      });
    }
  });

  logger.info('[compareVersionItems]', 'ID-based comparison completed', {
    type,
    addedCount: added.length,
    removedCount: removed.length,
    modifiedCount: modified.length,
    totalChanges: added.length + removed.length + modified.length,
    addedIds: added.map(item => item.item.id),
    removedIds: removed.map(item => item.item.id),
    modifiedIds: modified.map(item => item.item.id)
  });

  return {
    added,
    removed,
    modified
  };
};

/**
 * Prepare data with smart IDs for saving - this should be called before saving data
 * @param {Array} items - Items to prepare
 * @param {Array} previousItems - Previous version items for ID reference
 * @param {string} type - Data type ('cv', 'job', 'company')
 * @returns {Array} Items with IDs assigned
 */
export const prepareDataWithIds = (items, previousItems, type) => {
  return ensureItemIds(items, previousItems, type);
};

/**
 * Get a summary of changes between two versions
 * @param {Object} currentVersion - Current version
 * @param {Object} previousVersion - Previous version
 * @param {String} explicitType - Optional explicit type to use
 * @returns {String} Summary of changes
 */
export const getVersionChangeSummary = (currentVersion, previousVersion, explicitType) => {
  if (!currentVersion || !previousVersion) {
    return 'No previous version to compare';
  }

  // Determine the data type based on explicit type or version contents
  let type = explicitType;
  if (!type) {
    if (currentVersion.competencies) {
      type = 'cv';
    } else if (currentVersion.skills) {
      type = 'job';
    } else if (currentVersion.sections) {
      type = 'company';
    } else {
      logger.error('getVersionChangeSummary', 'Unknown version type, defaulting to cv', { currentVersion }, ErrorType.VALIDATION);
      type = 'cv';
    }
  }

  // Get the configuration for this data type
  const typeConfig = DATA_TYPES[type];
  const itemsKey = typeConfig.itemsKey;

  // Compare the versions
  const { added, removed, modified } = compareVersionItems(
    currentVersion[itemsKey] || [],
    previousVersion[itemsKey] || [],
    type
  );

  const totalChanges = added.length + removed.length + modified.length;

  if (totalChanges === 0) {
    return 'No changes detected';
  }

  const parts = [];
  if (added.length > 0) {
    parts.push(`${added.length} added`);
  }
  if (removed.length > 0) {
    parts.push(`${removed.length} removed`);
  }
  if (modified.length > 0) {
    parts.push(`${modified.length} modified`);
  }

  return parts.join(', ');
};

/**
 * Get a formatted description of what changed in a specific field
 * @param {Object} change - Change object with from and to properties
 * @param {String} fieldName - Name of the field that changed
 * @returns {String} Formatted description of the change
 */
export const getFieldChangeDescription = (change, fieldName) => {
  if (!change || !change.from || !change.to) {
    return '';
  }

  // Format the field name for display
  const formattedFieldName = fieldName.charAt(0).toUpperCase() + fieldName.slice(1);

  return `${formattedFieldName}: "${change.from}" → "${change.to}"`;
};

/**
 * Get a detailed description of all changes for a modified item
 * @param {Object} modifiedItem - Modified item with changes object
 * @returns {Array} Array of change descriptions
 */
export const getDetailedChangeDescriptions = (modifiedItem) => {
  if (!modifiedItem || !modifiedItem.changes) {
    return [];
  }

  const descriptions = [];

  Object.entries(modifiedItem.changes).forEach(([field, change]) => {
    descriptions.push(getFieldChangeDescription(change, field));
  });

  return descriptions;
};
